import React, { useEffect, useState, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { getFakeEvent, resolveObj } from '../../utils/utils'
import i18n from '../../translation/i18n'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
//defaultOptionIsSelcetable should always be in combination with noAutoSelect
const ArraySelect = ({
  returnEvent = false,
  name,
  options = [],
  disabledKeys = [],
  value,
  onChange = () => {},
  isInvalid,
  defaultOption = { value: '', name: i18n.t('pleaseSelect') },
  defaultOptionIsSelectable,
  otherValueKey,
  otherNameKey,
  selectKey,
  id,
  required,
  autoComplete,
  noAutoSelect,
  className,
  style,
  disabled,
}) => {
  const [loadingDotsIndex, setLoadingDotsIndex] = useState(0)
  const loadingDots = ['.', '..', '...']
  const { t } = useTranslation()
  const interval = useRef(null)
  const valueKey = otherValueKey ? otherValueKey : 'id'
  const nameKey = otherNameKey ? otherNameKey : 'displayName'

  const noOptionAvailable = options.length === 0
  const oneOptionAvailable = options.length === 1

  const safeValue = (v) => (v != null ? String(v) : '')

  const didAutoSelect = useRef(false)

  const handleAutoselect = useCallback(() => {
    if (!oneOptionAvailable || noAutoSelect) return

    const only = options[0]
    const selectedValue = safeValue(resolveObj(valueKey, only))
    const currentValue = safeValue(value)

    const isDefault = currentValue === safeValue(defaultOption.value)
    const notFoundInOptions = !options.some(
      (o) => safeValue(resolveObj(valueKey, o)) === currentValue
    )

    if (!value || isDefault || notFoundInOptions) {
      if (returnEvent) {
        onChange(getFakeEvent(name, selectedValue, 'select'))
      } else {
        onChange(only)
      }
    }
  }, [
    name,
    value,
    options,
    oneOptionAvailable,
    noAutoSelect,
    returnEvent,
    defaultOption,
    onChange,
    valueKey,
  ])

  useEffect(() => {
    didAutoSelect.current = false
    handleAutoselect()

    // wait for options
    if (noOptionAvailable && !interval.current) {
      interval.current = setInterval(() => {
        setLoadingDotsIndex((prev) => (prev + 1) % 3)
      }, 400)
    }

    return () => {
      clearInterval(interval.current)
    }
  }, [handleAutoselect, noOptionAvailable, options])

  //return option object onSelect
  const handleChange = (e) => {
    if (returnEvent) {
      onChange(getFakeEvent(e.target.name, e.target.value, 'select'))
      return
    }

    const val = safeValue(e.target.value)
    const found = options.find((obj) => safeValue(obj[valueKey]) === val)

    onChange(found ? found : defaultOption)
  }

  return (
    <Form.Select
      disabled={disabled || noOptionAvailable}
      name={name}
      onChange={handleChange}
      value={value || defaultOption.value}
      placeholder={loadingDots}
      isInvalid={isInvalid}
      key={selectKey}
      id={id}
      required={required}
      autoComplete={autoComplete}
      style={noOptionAvailable ? { color: '#c6c6c6', ...style } : { ...style }}
      className={className}
    >
      <option hidden={!defaultOptionIsSelectable} value={defaultOption.value}>
        {noOptionAvailable
          ? `(${t('noOptions')})${loadingDots[loadingDotsIndex]}`
          : defaultOption.name}
      </option>
      {options.map((option) => (
        <option
          key={`${resolveObj(valueKey, option)}-${resolveObj(nameKey, option)}`}
          disabled={disabledKeys.some(
            (key) => key === resolveObj(valueKey, option)
          )}
          value={resolveObj(valueKey, option)}
        >
          {resolveObj(nameKey, option)}
        </option>
      ))}
    </Form.Select>
  )
}

export default ArraySelect
